import * as PIXI from 'pixi.js';
import LayersManager from './layers/LayersManager';
import EventEmitter from './loaders/eventemitter3';
import AlignmentGuides from './utils/Draggable/AlignmentGuides';
import logger from './utils/logger';
import MediaManager from './utils/MediaManager';
import ProjectTimeline from './utils/ProjectTimeline';
import rafPauseTrigger from './utils/rafPauseTrigger';

const DEFAULT_BACKGROUND_COLOR = 0x000000;
const DEFAULT_MIN_FONT_SIZE = 10;

export const SCREEN_PADDING_PERCENT = 5;

/**
 * Renderer is the external interface for controlling what render engine displays
 */
export default class Renderer extends EventEmitter {
  static CLICK_ITEM = 'click-item';
  static MOVE_ITEM = 'move-item';
  static SCALE_ITEM = 'scale-item';
  static PAUSE = 'pause';
  static PLAY = 'play';
  static SEEKING = 'seeking';
  static SEEKED = 'seeked';
  static TIMECHANGE = 'timechange';

  static ERROR = 'error';
  static WARN = 'warn';
  static INFO = 'info';

  static _instance = null;

  static getInstance() {
    if (Renderer._instance === null) {
      Renderer._instance = new Renderer();
    }
    return this._instance;
  }

  static emit(event, ...args) {
    this._instance.emit(event, ...args);
  }

  canvas = document.createElement('canvas');
  _mediaManager = new MediaManager();
  _layersManager = new LayersManager();
  _projectTimeline = new ProjectTimeline();
  _mediaList = [];
  _reloading = false;

  get currentTime() {
    return this._projectTimeline.renderedTime;
  }

  get playing() {
    return this._projectTimeline.stopwatch.playing;
  }

  get projectDuration() {
    return this._projectTimeline.duration;
  }

  get seeking() {
    return this._projectTimeline.seeking;
  }

  get reloading() {
    return this._reloading;
  }

  // Indicates that we are resizing or otherwise altering the canvas in a way that requires us to clear it,
  // but that we will be reconstructing it immediately afterwards. For now, allows us to prevent the video
  // playback position from resetting when we don't want it to.
  set reloading(value) {
    this._reloading = value;
  }

  constructor() {
    super();

    this.minFontSize = DEFAULT_MIN_FONT_SIZE;

    // Use 1:1 resolution when rendering to canvas
    // so we get exactly the pixels we're expecting
    PIXI.settings.RESOLUTION = 1;
    this.pixi = new PIXI.Application({
      antialias: false,
      backgroundColor: DEFAULT_BACKGROUND_COLOR,
      preserveDrawingBuffer: true,
      resolution: 1,
      sharedTicker: true,
      view: this.canvas,
    });
    const antialiasFilter = new PIXI.filters.FXAAFilter();
    this.pixi.stage.filters = [antialiasFilter];
    this.pixi.stage.sortableChildren = true;

    // All Renderer instances run on the shared ticker.
    // Each renderer instance manages playback independently using a Stopwatch instance
    PIXI.Ticker.shared.add(this._render);

    // RAF loops including PIXI.Ticker get paused when the browser is offscreen
    // We want to pause playback when this happens
    // Otherwise media keeps playing past the end of the selected clip
    let shouldResume = this.playing;
    rafPauseTrigger(
      () => {
        shouldResume = this.playing;
        if (this.playing) this.pause();
      },
      () => {
        if (shouldResume) this.play();
      }
    );

    const endOfProjectListener = () =>
      this.pauseAt(this._projectTimeline.duration);
    this.on(Renderer.TIMECHANGE, endOfProjectListener, this);
  }

  /**
   * Clear out the internal state of a Renderer, destroying loaded assets.
   */
  clear() {
    this.pause();
    this.pixi.renderer.clear();
    this._projectTimeline.clear();
    this._mediaManager.clear();
    this._layersManager.clear();
    this._mediaList = [];
  }

  /**
   * Load MediaItem instances from models.
   *
   * loadProjectLayers is called multiple times per Renderer instance,
   * every time that the models are updated.
   *
   * MediaItem instances are loaded, updated, and destroyed as needed.
   */
  async loadProjectLayers(layers, options = {}) {
    const { minFontSize = DEFAULT_MIN_FONT_SIZE, isInteractive = true } =
      options;
    this.minFontSize = minFontSize;
    this.pixi.isInteractive = isInteractive;

    /**
     * NOTE: layers are passed in top-down, so we need to reverse the order
     * in order to add the correct zIndex values to the layer items.
     */
    const reversedLayers = [...layers].reverse();
    const mediaModelsWithLayers =
      this._layersManager.loadLayers(reversedLayers);

    /**
     * Static Assets and nested elements
     * must be loaded before all other models are
     * loaded since all models are loaded at once.
     */
    this._mediaList = await this._mediaManager.loadMediaFromModels(
      mediaModelsWithLayers,
      this.pixi
    );

    await this._projectTimeline.loadProject(this._mediaList, this.pixi);

    /**
     * Seek to requested timestamp after loading project. Needed
     * for seeking to accurate timestamp when adding an item
     * to the end of the project.
     */
    this._seekingTo && this.seek(this._seekingTo);
    this._seekingTo = null;
  }

  /**
   * Resize the canvas to new dimensions and reload project for new canvas dimensions
   */
  resize(width, height) {
    this.clear();
    this.pixi.renderer.resize(width, height);
    AlignmentGuides.resetInstance(this.pixi);
  }

  pause() {
    if (!this.playing) {
      return;
    }

    this._projectTimeline.pause();
    this.emit(Renderer.PAUSE);
  }

  pauseAt(timeToPause) {
    if (this.playing && this.currentTime >= timeToPause) {
      this.pause();
    }
  }

  mute() {
    this._mediaList.forEach((media) => media.mute());
  }

  unmute() {
    this._mediaList.forEach((media) => media.unmute());
  }

  setPlaybackVolume(volume) {
    this._mediaList.forEach((media) => media.setPlaybackVolume(volume));
  }

  async playItemOnce(item) {
    const { startTime, duration } = item;
    const endTime = startTime + duration;
    const temporaryPauseListener = () => this.pauseAt(endTime); // Named function necessary for detaching EventEmitter

    await this.seek(startTime);

    this.on(Renderer.TIMECHANGE, temporaryPauseListener, this)
      .once(
        Renderer.PAUSE,
        () => this.off(Renderer.TIMECHANGE, temporaryPauseListener, this),
        this
      )
      .once(
        Renderer.SEEKING,
        () => this.off(Renderer.TIMECHANGE, temporaryPauseListener, this),
        this
      );

    this.play();
  }

  play() {
    if (this.playing) {
      return;
    }
    this._projectTimeline.play();
    this.emit(Renderer.PLAY);
  }

  togglePlayPause() {
    if (this.playing) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * Seek to a given timestamp.
   * Return a callback that is resolved when seeking is completed
   */
  async seek(requestedTimestamp) {
    this._seekingTo = requestedTimestamp;
    const timestamp = Math.max(
      0,
      Math.min(this._projectTimeline.duration, requestedTimestamp)
    );

    if (timestamp === this.currentTime) {
      return;
    }

    this.emit(Renderer.SEEKING, timestamp);

    try {
      await this._projectTimeline.synchronizeMediaAndRender(timestamp, true);
      this.emit(Renderer.TIMECHANGE, timestamp);
    } catch (error) {
      logger.error('Error syncing project while seeking', { error });
    }

    this.emit(Renderer.SEEKED);
  }

  seekForward(duration) {
    this.seek(this.currentTime + duration);
  }

  seekBackward(duration) {
    this.seek(this.currentTime - duration);
  }

  seekToStart() {
    this.seek(0);
  }

  seekToEnd() {
    this.seek(this._projectTimeline.duration);
  }

  /**
   * The body of the render loop
   *
   * Render loop is controlled by PIXI.Ticker
   */
  _render = () => {
    if (!this._projectTimeline.readyForSync) {
      return;
    }

    try {
      this._projectTimeline.synchronizeMediaAndRender();
    } catch (error) {
      logger.error('Error in render loop while syncing media and rendering', {
        error,
      });
    }
    this.emit(Renderer.TIMECHANGE, this.currentTime);
  };
}
